import type { TherapySession, Therapist } from "@/types";

/**
 * Severity of a single vital sign.
 *
 * `neutral` is the resting state: a healthy therapist's whole strip reads
 * neutral, so any colour on the page is a real signal rather than decoration.
 * `idle` means there is not enough data to judge yet, which is different from
 * "fine" and must not be shown as if it were.
 */
export type VitalLevel = "idle" | "neutral" | "watch" | "alarm";

export interface Vital {
  /** Stable key, used for React keys and tests. */
  key: string;
  label: string;
  /** Headline value, already formatted for display. */
  value: string;
  /** Raw counts behind the value, shown on hover so the strip stays scannable. */
  detail: string;
  level: VitalLevel;
}

/** Minimum concluded sessions before a rate is meaningful enough to colour. */
const MIN_CONCLUDED_FOR_RATE = 5;
/** Minimum ratings before an average is meaningful enough to colour. */
const MIN_RATINGS_FOR_AVG = 3;
/** Minimum cancellations before attributing blame by share is meaningful. */
const MIN_CANCELLED_FOR_BLAME = 3;

const DAY_MS = 86_400_000;

export interface TherapistVitals {
  vitals: Vital[];
  /** Rating counts indexed 0..4 for 1..5 stars. */
  ratingDistribution: number[];
  ratedCount: number;
  avgRating: number | null;
  completedCount: number;
  upcomingCount: number;
  /** True when any vital is at `watch` or `alarm`. */
  hasSignal: boolean;
}

function concluded(sessions: TherapySession[]): TherapySession[] {
  return sessions.filter(
    (s) => s.status === "completed" || s.status === "cancelled" || s.status === "no_show"
  );
}

/**
 * A session counts as therapist-cancelled when `cancelledBy` is either the
 * literal sentinel written by the therapist-side handlers or the therapist's
 * own user id, which is what the generic cancel path stores.
 */
function isTherapistCancelled(session: TherapySession, therapistUserId: string): boolean {
  const by = session.cancelledBy;
  if (!by) return false;
  return by === "therapist" || by === therapistUserId;
}

function pct(numerator: number, denominator: number): string {
  if (denominator === 0) return "0%";
  return `${Math.round((numerator / denominator) * 100)}%`;
}

export function computeTherapistVitals(
  therapist: Therapist,
  sessions: TherapySession[],
  now: Date = new Date()
): TherapistVitals {
  const done = concluded(sessions);
  const completed = sessions.filter((s) => s.status === "completed");
  const cancelled = sessions.filter((s) => s.status === "cancelled");
  const noShow = sessions.filter((s) => s.status === "no_show");
  const upcoming = sessions.filter(
    (s) => s.status === "scheduled" && new Date(s.scheduledAt) > now
  );

  const rated = completed.filter((s) => typeof s.rating === "number" && s.rating > 0);
  const ratingDistribution = [0, 0, 0, 0, 0];
  for (const s of rated) {
    const bucket = (s.rating ?? 0) - 1;
    if (bucket >= 0 && bucket < ratingDistribution.length) ratingDistribution[bucket]!++;
  }
  const avgRating =
    rated.length > 0
      ? rated.reduce((sum, s) => sum + (s.rating ?? 0), 0) / rated.length
      : null;
  const lowRatings = ratingDistribution[0]! + ratingDistribution[1]!;

  const enoughConcluded = done.length >= MIN_CONCLUDED_FOR_RATE;

  // --- Completed sessions -------------------------------------------------
  const vitals: Vital[] = [
    {
      key: "completed",
      label: "Održane sesije",
      value: String(completed.length),
      detail:
        upcoming.length > 0
          ? `${upcoming.length} zakazanih u budućnosti`
          : "Nema zakazanih sesija u budućnosti",
      level: "neutral",
    },
  ];

  // --- Cancellation rate --------------------------------------------------
  const cancelRate = done.length > 0 ? cancelled.length / done.length : 0;
  vitals.push({
    key: "cancel-rate",
    label: "Otkazivanja",
    value: enoughConcluded ? pct(cancelled.length, done.length) : "–",
    detail: enoughConcluded
      ? `${cancelled.length} od ${done.length} završenih sesija`
      : `Premalo podataka, ${done.length} od ${MIN_CONCLUDED_FOR_RATE} potrebnih`,
    level: !enoughConcluded
      ? "idle"
      : cancelRate >= 0.3
        ? "alarm"
        : cancelRate >= 0.15
          ? "watch"
          : "neutral",
  });

  // --- Who cancels --------------------------------------------------------
  const byTherapist = cancelled.filter((s) =>
    isTherapistCancelled(s, therapist.userId)
  ).length;
  const blameShare = cancelled.length > 0 ? byTherapist / cancelled.length : 0;
  const enoughCancelled = cancelled.length >= MIN_CANCELLED_FOR_BLAME;
  vitals.push({
    key: "cancelled-by-therapist",
    label: "Otkazao terapeut",
    value: enoughCancelled ? pct(byTherapist, cancelled.length) : "–",
    detail: enoughCancelled
      ? `${byTherapist} od ${cancelled.length} otkazivanja`
      : `Premalo otkazivanja za ocjenu, ukupno ${cancelled.length}`,
    level: !enoughCancelled
      ? "idle"
      : blameShare >= 0.7
        ? "alarm"
        : blameShare >= 0.5
          ? "watch"
          : "neutral",
  });

  // --- No-show rate -------------------------------------------------------
  const noShowRate = done.length > 0 ? noShow.length / done.length : 0;
  vitals.push({
    key: "no-show-rate",
    label: "Nedolasci",
    value: enoughConcluded ? pct(noShow.length, done.length) : "–",
    detail: enoughConcluded
      ? `${noShow.length} od ${done.length} završenih sesija`
      : `Premalo podataka, ${done.length} od ${MIN_CONCLUDED_FOR_RATE} potrebnih`,
    level: !enoughConcluded
      ? "idle"
      : noShowRate >= 0.2
        ? "alarm"
        : noShowRate >= 0.1
          ? "watch"
          : "neutral",
  });

  // --- Average rating -----------------------------------------------------
  const enoughRatings = rated.length >= MIN_RATINGS_FOR_AVG;
  vitals.push({
    key: "avg-rating",
    label: "Prosječna ocjena",
    value: avgRating !== null && enoughRatings ? avgRating.toFixed(1) : "–",
    detail: enoughRatings
      ? `${rated.length} ocjena, ${lowRatings} niskih (1 ili 2)`
      : `Premalo ocjena, ${rated.length} od ${MIN_RATINGS_FOR_AVG} potrebnih`,
    level: !enoughRatings
      ? "idle"
      : avgRating! < 3.5
        ? "alarm"
        : avgRating! < 4.0 || lowRatings / rated.length >= 0.2
          ? "watch"
          : "neutral",
  });

  // --- Time since last session -------------------------------------------
  const lastCompleted = completed
    .map((s) => new Date(s.scheduledAt).getTime())
    .filter((t) => Number.isFinite(t) && t <= now.getTime())
    .sort((a, b) => b - a)[0];

  const isActive = therapist.status === "approved" && !therapist.deactivatedAt;
  if (lastCompleted === undefined) {
    vitals.push({
      key: "last-session",
      label: "Zadnja sesija",
      value: "–",
      detail: "Terapeut još nije održao nijednu sesiju",
      level: isActive && completed.length === 0 && upcoming.length === 0 ? "watch" : "idle",
    });
  } else {
    const days = Math.floor((now.getTime() - lastCompleted) / DAY_MS);
    vitals.push({
      key: "last-session",
      label: "Zadnja sesija",
      value: days === 0 ? "danas" : `prije ${days} d`,
      detail:
        upcoming.length > 0
          ? `Ima ${upcoming.length} zakazanih, pa pauza nije nužno problem`
          : "Nema zakazanih sesija u budućnosti",
      level: !isActive
        ? "idle"
        : days >= 60 && upcoming.length === 0
          ? "alarm"
          : days >= 30 && upcoming.length === 0
            ? "watch"
            : "neutral",
    });
  }

  return {
    vitals,
    ratingDistribution,
    ratedCount: rated.length,
    avgRating,
    completedCount: completed.length,
    upcomingCount: upcoming.length,
    hasSignal: vitals.some((v) => v.level === "watch" || v.level === "alarm"),
  };
}
